<template>
  <v-card class="pa-3">
    <h1 class="text-pagename mb-3">Promotion code management</h1>
    <v-layout row wrap class="mb-3">
      <v-flex xs12 sm4>
        <v-select
          :items="provinceItems"
          v-model="selectedProvince"
          item-text="text"
          item-value="value"
          label="Select province"
          prepend-icon="place"
          dense
          @change="onProvinceChange"
        ></v-select>
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex xs12 sm4>
        <div
          v-if="selectedProvince === '-'"
          class="all-province-banner elevation-1"
        >
          <v-icon small class="mr-2 globe-icon">public</v-icon>
          <span class="text">Showing <strong>All Provinces</strong></span>
        </div>
      </v-flex>
    </v-layout>
    <promotion-list-table
      ref="promoTable"
      :promotionListExternal="promotionList"
      :loadingExternal="loading"
      :province="selectedProvince !== '-' ? selectedProvince : null"
      :isAllProvince="selectedProvince === '-'"
    />
  </v-card>
</template>

<script>
// Lazy load the large table to reduce initial bundle weight
const PromotionListTable = () =>
  import(
    /* webpackChunkName: "promotion-list-table" */
    "@/components/back-office/PromotionAllProvince/PromotionListTable.vue"
  );
import { mapState } from "vuex";

export default {
  name: "PromotionListAllProvince",
  components: { PromotionListTable },
  data() {
    return {
      selectedProvince: "-", // '-' means all provinces
      promotionList: [],
      loading: false,
      provincesLoading: false,
    };
  },
  computed: {
    ...mapState({
      provinceData: (state) => state.fetchProvinceBack.provinceData,
    }),
    provinceItems() {
      const list = [{ text: "All Provinces", value: "-" }];
      if (!this.provinceData || this.provinceData.length === 0) return list;
      for (let p of this.provinceData) {
        // Use english name if available, fallback to thai
        const display = p.province_name_en || p.province_name_th;
        const value = this.normalizeProvince(display);
        list.push({ text: display, value });
      }
      return list;
    },
  },
  created() {
    this.setBreadcrumb();
    // Pre-apply query params for province before fetching
    const q = this.$route.query || {};
    if (q.province) {
      this.selectedProvince = q.province;
    }
    this.ensureProvinceData().then(() => {
      if (this.selectedProvince === "-" || !this.selectedProvince) {
        this.fetchAllProvinceList();
      } else {
        this.fetchProvinceList(this.selectedProvince);
      }
      this.$nextTick(() => {
        this.applyQueryToTable();
        this.setupChildWatchers();
      });
    });
  },
  methods: {
    setBreadcrumb() {
      const path = [
        {
          text: "Select province",
          disabled: false,
          href: "/weserve_admin_login/select/",
        },
        { text: "Promotion management", disabled: true },
      ];
      this.$store.commit("backOffice/saveRecentRoute", path);
    },
    normalizeProvince(name) {
      if (!name) return "";
      return name.toString().toLowerCase().replace(/[-\s]/g, "");
    },
    async ensureProvinceData() {
      if (!this.provinceData || this.provinceData.length === 0) {
        this.provincesLoading = true;
        await this.$store.dispatch("fetchProvinceBack/getData");
        this.provincesLoading = false;
      }
    },
    async fetchAllProvinceList() {
      // existing API for all provinces
      this.loading = true;

      let result = await this.$store.dispatch(
        "fetchFoodPromotionBack/fetchPromoListAllProvince"
      );

      this.promotionList = result.map((val) => {
        return {
          ...val,
          promotion_province_condition: JSON.parse(
            val.promotion_province_condition
          ),
        };
      }); // Deep clone to avoid Vue reactivity issues
      this.loading = false;
    },
    async fetchProvinceList(province) {
      this.loading = true;
      const res = await this.$store.dispatch(
        "fetchFoodPromotionBack/getPromotionList",
        province
      );
      // API returns object with data? In module commit uses raw data; so assign res if array
      if (res === false) {
        this.promotionList = [];
      } else if (Array.isArray(res)) {
        this.promotionList = res;
      } else if (res && Array.isArray(res.data)) {
        this.promotionList = res.data;
      } else {
        // Some endpoints (edit/delete) in module commit an array directly as 'data'
        if (res && typeof res === "object") {
          // Try to detect shape
          if (Array.isArray(res.promotion_list)) {
            this.promotionList = res.promotion_list;
          } else {
            this.promotionList = [];
          }
        } else {
          this.promotionList = [];
        }
      }
      this.loading = false;
    },
    onProvinceChange(val) {
      this.promotionList = [];
      if (!val || val === "-") {
        this.fetchAllProvinceList();
      } else {
        this.fetchProvinceList(val);
      }
      this.syncQuery();
    },
    // --- Query Sync Helpers ---
    applyQueryToTable() {
      const table = this.$refs.promoTable;
      if (!table) return;
      const q = this.$route.query || {};
      if (q.search !== undefined) table.search = q.search;
      if (q.orderType !== undefined)
        table.orderTypeFilter = q.orderType || "all";
      // Pagination (page & rows per page)
      if (table.pagination) {
        if (q.page && !isNaN(Number(q.page))) {
          table.pagination.page = Number(q.page);
        }
        if (q.rpp && !isNaN(Number(q.rpp))) {
          table.pagination.rowsPerPage = Number(q.rpp);
        }
      }
    },
    setupChildWatchers() {
      const table = this.$refs.promoTable;
      if (!table || this._childWatchersSetup) return;
      let t; // debounce for search typing
      const debounced = () => {
        clearTimeout(t);
        t = setTimeout(() => this.syncQuery(), 300);
      };
      this.$watch(() => table.search, debounced, { immediate: false });
      this.$watch(
        () => table.orderTypeFilter,
        () => this.syncQuery(),
        { immediate: false }
      );
      this.$watch(
        () => table.pagination && table.pagination.page,
        () => this.syncQuery(),
        { immediate: false }
      );
      this.$watch(
        () => table.pagination && table.pagination.rowsPerPage,
        () => this.syncQuery(),
        { immediate: false }
      );
      this._childWatchersSetup = true;
    },
    buildQueryState() {
      const table = this.$refs.promoTable;
      const q = {};
      // Province
      if (this.selectedProvince && this.selectedProvince !== "-") {
        q.province = this.selectedProvince;
      } else {
        q.province = "-";
      }
      if (table) {
        if (table.search) q.search = table.search;
        if (table.orderTypeFilter && table.orderTypeFilter !== "all") {
          q.orderType = table.orderTypeFilter;
        }
        if (table.pagination) {
          if (table.pagination.page && table.pagination.page !== 1) {
            q.page = table.pagination.page;
          }
          if (
            table.pagination.rowsPerPage &&
            ![25].includes(table.pagination.rowsPerPage)
          ) {
            q.rpp = table.pagination.rowsPerPage;
          }
        }
      }
      return q;
    },
    syncQuery() {
      // Merge with existing query only if changed to prevent redundant router ops
      const nextQ = this.buildQueryState();
      const current = this.$route.query || {};
      const changed = JSON.stringify(nextQ) !== JSON.stringify(current);
      if (!changed) return;
      this.$router.replace({ query: nextQ }).catch(() => {});
    },
  },
  beforeRouteUpdate(to, from, next) {
    next();
    // Apply new query after navigation completes
    this.$nextTick(() => {
      if (to.query.province !== from.query.province) {
        // Province changed via back/forward or manual URL entry
        this.selectedProvince = to.query.province || "-";
        if (this.selectedProvince === "-" || !this.selectedProvince) {
          this.fetchAllProvinceList();
        } else {
          this.fetchProvinceList(this.selectedProvince);
        }
      }
      // Apply search/filter/pagination
      this.applyQueryToTable();
    });
  },
};
</script>

<style scoped>
/* All provinces banner redesign */
.all-province-banner {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(90deg, #fffce2 0%, #fff5a8 100%);
  border-left: 5px solid #fddf00;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  letter-spacing: 0.3px;
  position: relative;
  overflow: hidden;
}
.all-province-banner:before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 90px;
  height: 100%;
  background: radial-gradient(
    circle at 30% 50%,
    #fddf00 0%,
    rgba(253, 223, 0, 0) 70%
  );
  opacity: 0.25;
  pointer-events: none;
}
.all-province-banner .globe-icon {
  background: #000;
  color: #fddf00 !important;
  border-radius: 50%;
  padding: 4px;
  box-shadow: 0 0 0 2px #00000010;
}
.all-province-banner .text strong {
  color: #000;
  font-weight: 600;
}
@media (max-width: 600px) {
  .all-province-banner {
    width: 100%;
    justify-content: flex-start;
    font-size: 12px;
    padding: 8px 12px;
  }
}
</style>
